import * as Sentry from "@sentry/react";
import { track } from "client/src/utils/analytics";
import { useCallback, useRef } from "react";
import type { Factor } from "client/src/auth/oktaAuth";
import type { SegmentEvent } from "client/src/utils/analytics";

export type MFAEnrollmentStep = "enroll" | "activate" | "resend";

type EnrollmentFlow = {
  flowId: string;
  startedAt: number;
  initialStatus: "MFA_ENROLL" | "MFA_ENROLL_ACTIVATE";
  oktaUserId?: string;
  factorType?: Factor["factorType"];
  activationAttempts: number;
  failedSteps: number;
};

const report = (
  event: Extract<SegmentEvent, `MFA Enrollment ${string}`>,
  flow: EnrollmentFlow,
  properties?: { step: MFAEnrollmentStep; errorCode?: string },
) => {
  const { startedAt, ...flowProperties } = flow;
  // Analytics failures must not interrupt authentication.
  void track(event, {
    ...flowProperties,
    elapsedMs: Math.max(0, Date.now() - startedAt),
    ...properties,
  }).catch((error: unknown) => Sentry.captureException(error));
};

export const useMFAEnrollmentAnalytics = () => {
  const flow = useRef<EnrollmentFlow | undefined>(undefined);

  const start = useCallback(
    (
      initialStatus: EnrollmentFlow["initialStatus"],
      oktaUserId: unknown,
      factorType?: Factor["factorType"],
    ) => {
      if (flow.current) return;
      flow.current = {
        flowId: crypto.randomUUID(),
        startedAt: Date.now(),
        initialStatus,
        oktaUserId: typeof oktaUserId === "string" ? oktaUserId : undefined,
        factorType,
        activationAttempts: 0,
        failedSteps: 0,
      };
      report("MFA Enrollment Started", flow.current);
    },
    [],
  );

  const submit = useCallback((step: MFAEnrollmentStep, factorType?: Factor["factorType"]) => {
    if (!flow.current) return;
    if (factorType) flow.current.factorType = factorType;
    if (step === "activate") flow.current.activationAttempts += 1;
    report("MFA Enrollment Step Submitted", flow.current, { step });
  }, []);

  const fail = useCallback((step: MFAEnrollmentStep, error?: unknown) => {
    if (!flow.current) return;
    flow.current.failedSteps += 1;
    const errorCode =
      typeof error === "object" &&
      error !== null &&
      "errorCode" in error &&
      typeof error.errorCode === "string" &&
      /^E\d{7}$/.test(error.errorCode)
        ? error.errorCode
        : "unknown";
    report("MFA Enrollment Step Failed", flow.current, { step, errorCode });
  }, []);

  const complete = useCallback(() => {
    if (!flow.current) return;
    report("MFA Enrollment Completed", flow.current);
    flow.current = undefined;
  }, []);

  const reset = useCallback(() => {
    flow.current = undefined;
  }, []);

  return { start, submit, fail, complete, reset };
};
